
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AdminSettings } from "@/types/adminSettings";
import { Palette, Save } from "lucide-react";

interface BrandTabProps {
  settings: AdminSettings;
  updateSettings: (path: string[], value: any) => void;
  saveSettings: () => Promise<void>;
  isSaving: boolean;
}

export const BrandTab = ({ settings, updateSettings, saveSettings, isSaving }: BrandTabProps) => {
  const handleColorChange = (colorKey: string, value: string) => {
    updateSettings(['brand', 'colors', colorKey], value);
    updateSettings(['brand', 'lastUpdated'], new Date().toISOString());
  };

  const resetToDefaults = () => {
    updateSettings(['brand', 'colors', 'color1'], "#165a54");
    updateSettings(['brand', 'colors', 'color2'], "#23643D");
    updateSettings(['brand', 'colors', 'color3'], "#91DA73");
    updateSettings(['brand', 'colors', 'color4'], "#0D2D23");
    updateSettings(['brand', 'colors', 'color5'], "#F5FBEF");
    updateSettings(['brand', 'lastUpdated'], new Date().toISOString());
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Brand Colors
          </CardTitle>
          <CardDescription>
            Configure the brand colors used throughout the application
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label htmlFor="color1">Color 1 - Buttons</Label>
              <div className="flex gap-2">
                <Input
                  id="color1"
                  type="text"
                  value={settings.brand?.colors?.color1 || "#165a54"}
                  onChange={(e) => handleColorChange('color1', e.target.value)}
                  placeholder="#165a54"
                  className="font-mono"
                />
                <div 
                  className="w-12 h-10 rounded border-2 border-gray-300"
                  style={{ backgroundColor: settings.brand?.colors?.color1 || "#165a54" }}
                />
              </div>
              <p className="text-sm text-gray-500">Used for primary buttons and CTAs</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="color2">Color 2 - Highlighted Text</Label>
              <div className="flex gap-2">
                <Input
                  id="color2"
                  type="text"
                  value={settings.brand?.colors?.color2 || "#23643D"}
                  onChange={(e) => handleColorChange('color2', e.target.value)}
                  placeholder="#23643D"
                  className="font-mono"
                />
                <div 
                  className="w-12 h-10 rounded border-2 border-gray-300"
                  style={{ backgroundColor: settings.brand?.colors?.color2 || "#23643D" }}
                />
              </div>
              <p className="text-sm text-gray-500">Used for highlighted text and loading animations</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="color3">Color 3 - Background</Label>
              <div className="flex gap-2">
                <Input
                  id="color3"
                  type="text"
                  value={settings.brand?.colors?.color3 || "#91DA73"}
                  onChange={(e) => handleColorChange('color3', e.target.value)}
                  placeholder="#91DA73"
                  className="font-mono"
                />
                <div 
                  className="w-12 h-10 rounded border-2 border-gray-300"
                  style={{ backgroundColor: settings.brand?.colors?.color3 || "#91DA73" }}
                />
              </div>
              <p className="text-sm text-gray-500">Used for accent backgrounds and highlights</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="color4">Color 4 - Headings</Label>
              <div className="flex gap-2">
                <Input
                  id="color4"
                  type="text"
                  value={settings.brand?.colors?.color4 || "#0D2D23"}
                  onChange={(e) => handleColorChange('color4', e.target.value)}
                  placeholder="#0D2D23"
                  className="font-mono"
                />
                <div 
                  className="w-12 h-10 rounded border-2 border-gray-300"
                  style={{ backgroundColor: settings.brand?.colors?.color4 || "#0D2D23" }}
                />
              </div>
              <p className="text-sm text-gray-500">Used for headings and titles</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="color5">Color 5 - Light Background</Label>
              <div className="flex gap-2">
                <Input
                  id="color5"
                  type="text"
                  value={settings.brand?.colors?.color5 || "#F5FBEF"}
                  onChange={(e) => handleColorChange('color5', e.target.value)}
                  placeholder="#F5FBEF"
                  className="font-mono"
                />
                <div 
                  className="w-12 h-10 rounded border-2 border-gray-300"
                  style={{ backgroundColor: settings.brand?.colors?.color5 || "#F5FBEF" }}
                />
              </div>
              <p className="text-sm text-gray-500">Used for light background sections</p>
            </div>
          </div>

          <div className="border-t pt-4">
            <div className="flex justify-between items-center">
              <div>
                <h4 className="font-medium">Preview</h4>
                <p className="text-sm text-gray-500">See how your colors look in action</p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={resetToDefaults}
                  disabled={isSaving}
                >
                  Reset to Defaults
                </Button>
                <Button
                  onClick={saveSettings}
                  disabled={isSaving}
                  style={{ backgroundColor: settings.brand?.colors?.color1 || "#165a54" }}
                  className="text-white hover:opacity-90"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isSaving ? "Saving..." : "Save Colors"}
                </Button>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h5 className="font-medium mb-3">Color Preview</h5>
            <div className="space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <button 
                  className="px-4 py-2 rounded text-white font-medium"
                  style={{ backgroundColor: settings.brand?.colors?.color1 || "#165a54" }}
                >
                  Primary Button
                </button>
                <span 
                  className="font-semibold"
                  style={{ color: settings.brand?.colors?.color2 || "#23643D" }}
                >
                  Highlighted Text
                </span>
                <h4 
                  className="font-bold text-lg"
                  style={{ color: settings.brand?.colors?.color4 || "#0D2D23" }}
                >
                  Heading Example
                </h4>
              </div>
              <div 
                className="p-3 rounded border"
                style={{ backgroundColor: settings.brand?.colors?.color3 || "#91DA73" }}
              >
                <span className="text-gray-800">Background accent example</span>
              </div>
              <div 
                className="p-3 rounded border"
                style={{ backgroundColor: settings.brand?.colors?.color5 || "#F5FBEF" }}
              >
                <span className="text-gray-800">Light background example</span>
              </div>
            </div>
          </div>

          {settings.brand?.lastUpdated && (
            <div className="text-sm text-gray-500">
              Last updated: {new Date(settings.brand.lastUpdated).toLocaleString()}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
